/**
 * Sistema de carga automática de módulos por página
 *
 * Este módulo detecta automáticamente qué página está cargada y carga
 * el módulo JavaScript correspondiente si existe.
 *
 * USO:
 * 1. Añadir data-page="nombre-pagina" al <body> desde PHP
 * 2. Crear el módulo en _dev/assets/pages/{nombre-pagina}.js
 * 3. El módulo debe exportar una función init{NombrePagina}()
 *
 * El sistema también funciona con Turbo, recargando módulos al navegar.
 */

// Registro de módulos disponibles
// Mapea data-page -> función de importación dinámica
const pageModules = {
    // Módulos existentes
    'home': () => import(/* webpackChunkName: "home" */ './pages/home.js'),
    'home-index2': () => import(/* webpackChunkName: "home" */ './pages/home-index2.js'),
    'facturas': () => import(/* webpackChunkName: "facturas" */ './pages/facturas.js'),
    'presupuestos': () => import(/* webpackChunkName: "presupuestos" */ './pages/presupuestos.js'),
    'gastos': () => import(/* webpackChunkName: "gastos" */ './pages/gastos.js'),
    'facturas-emitidas': () => import(/* webpackChunkName: "facturas-emitidas" */ './pages/facturas-emitidas.js'),
    'facturas_emitidas': () => import(/* webpackChunkName: "facturas-emitidas" */ './pages/facturas-emitidas.js'),
    'facturas-recibidas': () => import(/* webpackChunkName: "facturas-recibidas" */ './pages/facturas-recibidas.js'),
    'facturas_recibidas': () => import(/* webpackChunkName: "facturas-recibidas" */ './pages/facturas-recibidas.js'),
    'contactos': () => import(/* webpackChunkName: "contactos" */ './pages/contactos.js'),
    'clientes': () => import(/* webpackChunkName: "contactos" */ './pages/contactos.js'), // Alias

    // Módulo de ejemplo - ausencias
    'ausencias': () => import(/* webpackChunkName: "ausencias" */ './pages/ausencias.js'),

    // Módulo de productos
    'productos': () => import(/* webpackChunkName: "productos" */ './pages/productos.js'),

    // Módulos de autenticación
    'login': () => import(/* webpackChunkName: "auth" */ './pages/login.js'),
    'register': () => import(/* webpackChunkName: "auth" */ './pages/register.js'),

    // Módulos de settings
    'settings-logo': () => import(/* webpackChunkName: "settings" */ './pages/settings-logo.js'),
    'settings_logo': () => import(/* webpackChunkName: "settings" */ './pages/settings-logo.js'),
    'settings-security': () => import(/* webpackChunkName: "settings" */ './pages/settings-security.js'),
    'settings_security': () => import(/* webpackChunkName: "settings" */ './pages/settings-security.js'),
    'settings-email': () => import(/* webpackChunkName: "settings" */ './pages/settings-email.js'),
    'settings_email': () => import(/* webpackChunkName: "settings" */ './pages/settings-email.js'),
    'settings-whatsapp': () => import(/* webpackChunkName: "settings" */ './pages/settings-whatsapp.js'),
    'settings_whatsapp': () => import(/* webpackChunkName: "settings" */ './pages/settings-whatsapp.js'),
    'settings-paymentmethods': () => import(/* webpackChunkName: "settings" */ './pages/settings-paymentmethods.js'),
    'settings_paymentmethods': () => import(/* webpackChunkName: "settings" */ './pages/settings-paymentmethods.js'),
    'settings-paypal': () => import(/* webpackChunkName: "settings" */ './pages/settings-paypal.js'),
    'settings_paypal': () => import(/* webpackChunkName: "settings" */ './pages/settings-paypal.js'),
    'settings-formats': () => import(/* webpackChunkName: "settings" */ './pages/settings-formats.js'),
    'settings_formats': () => import(/* webpackChunkName: "settings" */ './pages/settings-formats.js'),
    'settings-preferences': () => import(/* webpackChunkName: "settings" */ './pages/settings-preferences.js'),
    'settings_preferences': () => import(/* webpackChunkName: "settings" */ './pages/settings-preferences.js'),
    'settings-serials': () => import(/* webpackChunkName: "settings" */ './pages/settings-serials.js'),
    'settings_serials': () => import(/* webpackChunkName: "settings" */ './pages/settings-serials.js'),
    'settings-business': () => import(/* webpackChunkName: "settings" */ './pages/settings-business.js'),
    'settings_business': () => import(/* webpackChunkName: "settings" */ './pages/settings-business.js'),
    'settings-themes': () => import(/* webpackChunkName: "settings" */ './pages/settings-themes.js'),
    'settings_themes': () => import(/* webpackChunkName: "settings" */ './pages/settings-themes.js'),
    'settings-plans': () => import(/* webpackChunkName: "settings" */ './pages/settings-plans.js'),
    'settings_plans': () => import(/* webpackChunkName: "settings" */ './pages/settings-plans.js'),
    'settings-limites': () => import(/* webpackChunkName: "settings" */ './pages/settings-limites.js'),
    'settings_limites': () => import(/* webpackChunkName: "settings" */ './pages/settings-limites.js'),

    // Módulos de documentos
    'documento-nuevo': () => import(/* webpackChunkName: "documento" */ './pages/documento-nuevo.js'),
    'documento-nuevo2': () => import(/* webpackChunkName: "documento" */ './pages/documento-nuevo.js').then(m => ({ initDocumentoNuevo2: m.initDocumentoNuevo })),
    'documento_nuevo': () => import(/* webpackChunkName: "documento" */ './pages/documento-nuevo.js'),
    'documento_nuevo2': () => import(/* webpackChunkName: "documento" */ './pages/documento-nuevo.js').then(m => ({ initDocumentoNuevo2: m.initDocumentoNuevo })),
    'documento-ed': () => import(/* webpackChunkName: "documento" */ './pages/documento-ed.js'),
    'documento-ed2': () => import(/* webpackChunkName: "documento" */ './pages/documento-ed.js'),
    'documento_ed': () => import(/* webpackChunkName: "documento" */ './pages/documento-ed.js'),
    'documento_ed2': () => import(/* webpackChunkName: "documento" */ './pages/documento-ed.js'),
    'documento-editarnomina': () => import(/* webpackChunkName: "documento" */ './pages/documento-editarnomina.js'),
    'documento_editarnomina': () => import(/* webpackChunkName: "documento" */ './pages/documento-editarnomina.js'),
    'documento-edgasto': () => import(/* webpackChunkName: "documento-gasto" */ './pages/documento-edgasto.js'),
    'documento_edgasto': () => import(/* webpackChunkName: "documento-gasto" */ './pages/documento-edgasto.js'),
    'documento-edemitida': () => import(/* webpackChunkName: "documento-edemitida" */ './pages/documento-edemitida.js'),
    'documento_edemitida': () => import(/* webpackChunkName: "documento-edemitida" */ './pages/documento-edemitida.js'),
    'documento-nuevogasto': () => import(/* webpackChunkName: "documento-gasto" */ './pages/documento-nuevogasto.js'),
    'documento_nuevogasto': () => import(/* webpackChunkName: "documento-gasto" */ './pages/documento-nuevogasto.js'),

    // Módulos de páginas principales
    'remesas': () => import(/* webpackChunkName: "remesas" */ './pages/remesas.js'),
    'remesas-nuevo-cobro': () => import(/* webpackChunkName: "remesas" */ './pages/remesas-nuevo-cobro.js'),
    'remesas-nuevo_cobro': () => import(/* webpackChunkName: "remesas" */ './pages/remesas-nuevo-cobro.js'),
    'remesas_nuevo_cobro': () => import(/* webpackChunkName: "remesas" */ './pages/remesas-nuevo-cobro.js'),
    'remesas-nuevo-pago': () => import(/* webpackChunkName: "remesas" */ './pages/remesas-nuevo-pago.js'),
    'remesas-nuevo_pago': () => import(/* webpackChunkName: "remesas" */ './pages/remesas-nuevo-pago.js'),
    'remesas_nuevo_pago': () => import(/* webpackChunkName: "remesas" */ './pages/remesas-nuevo-pago.js'),
    'remesas-import': () => import(/* webpackChunkName: "remesas-import" */ './pages/remesas-import.js'),
    'remesas_import': () => import(/* webpackChunkName: "remesas-import" */ './pages/remesas-import.js'),

    // Módulo Suscripciones (Stripe Connect recurrente)
    'suscripciones': () => import(/* webpackChunkName: "suscripciones" */ './pages/suscripciones.js'),
    'suscripciones-item': () => import(/* webpackChunkName: "suscripciones" */ './pages/suscripciones-item.js'),
    'suscripciones_item': () => import(/* webpackChunkName: "suscripciones" */ './pages/suscripciones-item.js'),
    'trazabilidad': () => import(/* webpackChunkName: "trazabilidad" */ './pages/trazabilidad.js'),
    'fichajes': () => import(/* webpackChunkName: "fichajes" */ './pages/fichajes.js'),
    'perfil': () => import(/* webpackChunkName: "perfil" */ './pages/perfil.js'),
    'exportar': () => import(/* webpackChunkName: "exportar" */ './pages/exportar.js'),

    // Módulos de exportación específicos
    'exportar-a3asesor-eco-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-a3asesor-eco.js'),
    'exportar_a3asesor-eco-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-a3asesor-eco.js'),
    'exportar-contasol-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-contasol.js'),
    'exportar_contasol-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-contasol.js'),
    'exportar-diamacon-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-diamacon.js'),
    'exportar_diamacon-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-diamacon.js'),
    'exportar-contasol-estimacion-directa-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-contasol-directa.js'),
    'exportar_contasol-estimacion-directa-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-contasol-directa.js'),
    'exportar-golden-soft-estimacion-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-golden-soft-estimacion.js'),
    'exportar_golden-soft-estimacion-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-golden-soft-estimacion.js'),
    'exportar-golden-soft-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-golden-soft.js'),
    'exportar_golden-soft-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-golden-soft.js'),
    'exportar-aplifisa-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-aplifisa.js'),
    'exportar_aplifisa-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-aplifisa.js'),
    'exportar-aplifisa-fiscal-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-aplifisa-fiscal.js'),
    'exportar_aplifisa-fiscal-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-aplifisa-fiscal.js'),
    'exportar-aplicaciones-informaticas-tributarias-factura-emitida': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-ait-emitidas.js'),
    'exportar_aplicaciones-informaticas-tributarias-factura-emitida': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-ait-emitidas.js'),
    'exportar-aplicaciones-informaticas-tributarias-factura-recibida': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-ait-recibidas.js'),
    'exportar_aplicaciones-informaticas-tributarias-factura-recibida': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-ait-recibidas.js'),
    'exportar-ncs-fiscal-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-ncs-fiscal.js'),
    'exportar_ncs-fiscal-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-ncs-fiscal.js'),
    'exportar-ncs-contabilidad-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-ncs-contabilidad.js'),
    'exportar_ncs-contabilidad-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-ncs-contabilidad.js'),
    'exportar-sage-despachos-connected-facturas-emitidas-y-recibidas-plugin-facturas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-sage-despachos.js'),
    'exportar_sage-despachos-connected-facturas-emitidas-y-recibidas-plugin-facturas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-sage-despachos.js'),
    'exportar-sage-contaplus-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-sage-contaplus.js'),
    'exportar_sage-contaplus-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-sage-contaplus.js'),
    'exportar-siga21-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-siga21.js'),
    'exportar_siga21-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-siga21.js'),
    'exportar-club-del-asesor-factura-emitida': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-club-del-asesor-emitidas.js'),
    'exportar_club-del-asesor-factura-emitida': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-club-del-asesor-emitidas.js'),
    'exportar-club-del-asesor-factura-recibida': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-club-del-asesor-recibidas.js'),
    'exportar_club-del-asesor-factura-recibida': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-club-del-asesor-recibidas.js'),
    'exportar-delta-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-delta.js'),
    'exportar_delta-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-delta.js'),
    'exportar-delta-edyo-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-delta-edyo.js'),
    'exportar_delta-edyo-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-delta-edyo.js'),
    'exportar-aig-classicconta-factura-emitida': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-aig-classicconta-emitidas.js'),
    'exportar_aig-classicconta-factura-emitida': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-aig-classicconta-emitidas.js'),
    'exportar-aig-classicconta-factura-recibida': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-aig-classicconta-recibidas.js'),
    'exportar_aig-classicconta-factura-recibida': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-aig-classicconta-recibidas.js'),
    'exportar-aig-classicconta-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-aig-classicconta.js'),
    'exportar_aig-classicconta-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-aig-classicconta.js'),
    'exportar-miconta-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-miconta.js'),
    'exportar_miconta-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-miconta.js'),
    'exportar-microdata-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-microdata.js'),
    'exportar_microdata-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-microdata.js'),
    'exportar-microdata-facturas-emitidas-y-recibidas-msbabelia-excel': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-microdata-excel.js'),
    'exportar_microdata-facturas-emitidas-y-recibidas-msbabelia-excel': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-microdata-excel.js'),
    'exportar-mieos-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-mieos.js'),
    'exportar_mieos-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-mieos.js'),
    'exportar-visual-nt-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-visual-nt.js'),
    'exportar_visual-nt-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-visual-nt.js'),
    'exportar-visueo-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-visueo.js'),
    'exportar_visueo-facturas-emitidas-y-recibidas': () => import(/* webpackChunkName: "exportar" */ './pages/exportar-visueo.js'),

    // Módulo de solicitud de asesor
    'advisor-request': () => import(/* webpackChunkName: "partners" */ './pages/advisor-request.js'),

    // Módulos adicionales
    'partners': () => import(/* webpackChunkName: "partners" */ './pages/partners.js'),
    'quarter': () => import(/* webpackChunkName: "quarter" */ './pages/quarter.js'),
    'accounting': () => import(/* webpackChunkName: "accounting" */ './pages/accounting.js'),
    'contabilidad-libro-diario': () => import(/* webpackChunkName: "contabilidad-libro-diario" */ './pages/contabilidad-libro-diario.js'),
    'contabilidad_libro-diario': () => import(/* webpackChunkName: "contabilidad-libro-diario" */ './pages/contabilidad-libro-diario.js'),
    'contabilidad-cuadro-de-cuentas': () => import(/* webpackChunkName: "contabilidad-cuadro-de-cuentas" */ './pages/contabilidad-cuadro-de-cuentas.js'),
    'contabilidad_cuadro-de-cuentas': () => import(/* webpackChunkName: "contabilidad-cuadro-de-cuentas" */ './pages/contabilidad-cuadro-de-cuentas.js'),
    'contabilidad-libro-diario-import': () => import(/* webpackChunkName: "contabilidad-libro-diario-import" */ './pages/contabilidad-libro-diario-import.js'),
    'contabilidad_libro-diario_import': () => import(/* webpackChunkName: "contabilidad-libro-diario-import" */ './pages/contabilidad-libro-diario-import.js'),
    'contabilidad-informes-objetivos': () => import(/* webpackChunkName: "contabilidad-informes" */ './pages/contabilidad-informes-objetivos.js'),
    'contabilidad_informes_objetivos': () => import(/* webpackChunkName: "contabilidad-informes" */ './pages/contabilidad-informes-objetivos.js'),
    'contabilidad-informes-perdidas-ganancias': () => import(/* webpackChunkName: "contabilidad-informes" */ './pages/contabilidad-informes-perdidas-ganancias.js'),
    'contabilidad-informes-balance-sumas-saldos': () => import(/* webpackChunkName: "contabilidad-informes" */ './pages/contabilidad-informes-balance-sumas-saldos.js'),
    'contabilidad-informes-balance-situacion': () => import(/* webpackChunkName: "contabilidad-informes" */ './pages/contabilidad-informes-balance-situacion.js'),
    'contabilidad-informes-ecpn': () => import(/* webpackChunkName: "contabilidad-informes" */ './pages/contabilidad-informes-ecpn.js'),
    'contabilidad-informes-efe': () => import(/* webpackChunkName: "contabilidad-informes" */ './pages/contabilidad-informes-efe.js'),
    'contabilidad-informes-libro-facturas-emitidas': () => import(/* webpackChunkName: "contabilidad-informes" */ './pages/contabilidad-informes-libro-facturas-emitidas.js'),
    'contabilidad-informes-libro-facturas-recibidas': () => import(/* webpackChunkName: "contabilidad-informes" */ './pages/contabilidad-informes-libro-facturas-recibidas.js'),
    'contabilidad-informes-libro-mayor': () => import(/* webpackChunkName: "contabilidad-informes" */ './pages/contabilidad-informes-libro-mayor.js'),
    'contabilidad-informes-banco': () => import(/* webpackChunkName: "contabilidad-informes" */ './pages/contabilidad-informes-banco.js'),
    'contabilidad_informes_banco': () => import(/* webpackChunkName: "contabilidad-informes" */ './pages/contabilidad-informes-banco.js'),
    'contabilidad-cierre-ejercicio': () => import(/* webpackChunkName: "contabilidad-cierre-ejercicio" */ './pages/contabilidad-cierre-ejercicio.js'),
    'informes-ventas': () => import(/* webpackChunkName: "informes-ventas" */ './pages/informes-ventas.js'),
    'informes_ventas': () => import(/* webpackChunkName: "informes-ventas" */ './pages/informes-ventas.js'),
    'informes-compras': () => import(/* webpackChunkName: "informes-compras" */ './pages/informes-compras.js'),
    'informes_compras': () => import(/* webpackChunkName: "informes-compras" */ './pages/informes-compras.js'),
    'informes-gastos': () => import(/* webpackChunkName: "informes-gastos" */ './pages/informes-gastos.js'),
    'informes_gastos': () => import(/* webpackChunkName: "informes-gastos" */ './pages/informes-gastos.js'),
    'informes-presupuestos': () => import(/* webpackChunkName: "informes-presupuestos" */ './pages/informes-presupuestos.js'),
    'informes_presupuestos': () => import(/* webpackChunkName: "informes-presupuestos" */ './pages/informes-presupuestos.js'),
    'informes-proformas': () => import(/* webpackChunkName: "informes-proformas" */ './pages/informes-proformas.js'),
    'informes_proformas': () => import(/* webpackChunkName: "informes-proformas" */ './pages/informes-proformas.js'),
    'albaran': () => import(/* webpackChunkName: "albaran" */ './pages/albaran.js'),

    // Módulos de clientes
    'clientes-nuevo': () => import(/* webpackChunkName: "clientes" */ './pages/clientes-nuevo.js'),
    'clientes_nuevo': () => import(/* webpackChunkName: "clientes" */ './pages/clientes-nuevo.js'),
    'clientes-ed': () => import(/* webpackChunkName: "clientes" */ './pages/clientes-ed.js'),
    'clientes_ed': () => import(/* webpackChunkName: "clientes" */ './pages/clientes-ed.js'),

    // Módulos de proveedores
    'proveedores-nuevo': () => import(/* webpackChunkName: "proveedores" */ './pages/proveedores-nuevo.js'),
    'proveedores_nuevo': () => import(/* webpackChunkName: "proveedores" */ './pages/proveedores-nuevo.js'),
    'proveedores-ed': () => import(/* webpackChunkName: "proveedores" */ './pages/proveedores-ed.js'),
    'proveedores_ed': () => import(/* webpackChunkName: "proveedores" */ './pages/proveedores-ed.js'),

    // Módulos de productos (crear/editar)
    'productos-nuevo': () => import(/* webpackChunkName: "productos-forms" */ './pages/productos-nuevo.js'),
    'productos_nuevo': () => import(/* webpackChunkName: "productos-forms" */ './pages/productos-nuevo.js'),
    'productos-ed': () => import(/* webpackChunkName: "productos-forms" */ './pages/productos-ed.js'),
    'productos_ed': () => import(/* webpackChunkName: "productos-forms" */ './pages/productos-ed.js'),
    'productos-import': () => import(/* webpackChunkName: "productos-forms" */ './pages/productos-import.js'),
    'productos_import': () => import(/* webpackChunkName: "productos-forms" */ './pages/productos-import.js'),
    'cuentas-import': () => import(/* webpackChunkName: "cuentas-import" */ './pages/cuentas-import.js'),
    'cuentas_import': () => import(/* webpackChunkName: "cuentas-import" */ './pages/cuentas-import.js'),

    // Módulo de recuperación de contraseña
    'forgotpassword': () => import(/* webpackChunkName: "auth" */ './pages/forgotpassword.js'),

    // Módulo de notificaciones
    'settings-notifications': () => import(/* webpackChunkName: "settings" */ './pages/settings-notifications.js'),
    'settings_notifications': () => import(/* webpackChunkName: "settings" */ './pages/settings-notifications.js'),

    // Módulo de lectura/importación de gastos
    'gastos-reader': () => import(/* webpackChunkName: "gastos-reader" */ './pages/gastos-reader.js'),
    'gastos_reader': () => import(/* webpackChunkName: "gastos-reader" */ './pages/gastos-reader.js'),

    // Módulo de lectura/importación de documentos (scanner)
    'documentos-scanner': () => import(/* webpackChunkName: "documentos-scanner" */ './pages/documentos-scanner.js'),
    'documentos_scanner': () => import(/* webpackChunkName: "documentos-scanner" */ './pages/documentos-scanner.js'),

    // Módulo de juntar facturas (merge invoices)
    'juntar-facturas': () => import(/* webpackChunkName: "juntar-facturas" */ './pages/juntar-facturas.js'),
    'juntar_facturas': () => import(/* webpackChunkName: "juntar-facturas" */ './pages/juntar-facturas.js'),

    // Módulo de invitación/join
    'join': () => import(/* webpackChunkName: "join" */ './pages/join.js'),

    // Módulo de suscripción
    'settings-suscription': () => import(/* webpackChunkName: "settings" */ './pages/settings-suscription.js'),
    'settings_suscription': () => import(/* webpackChunkName: "settings" */ './pages/settings-suscription.js'),

    // Módulo de actividades
    'settings-activities': () => import(/* webpackChunkName: "settings" */ './pages/settings-activities.js'),
    'settings_activities': () => import(/* webpackChunkName: "settings" */ './pages/settings-activities.js'),

    // Módulo de trazabilidad item (edición de gastos)
    'trazabilidad-item': () => import(/* webpackChunkName: "trazabilidad" */ './pages/trazabilidad-item.js'),
    'trazabilidad_item': () => import(/* webpackChunkName: "trazabilidad" */ './pages/trazabilidad-item.js'),
    'trazabilidad-reader': () => import(/* webpackChunkName: "trazabilidad" */ './pages/trazabilidad-reader.js'),
    'trazabilidad_reader': () => import(/* webpackChunkName: "trazabilidad" */ './pages/trazabilidad-reader.js'),

    // Módulo de nuevo ticket de gasto
    'nuevoticketgasto': () => import(/* webpackChunkName: "gastos" */ './pages/nuevoticketgasto.js'),

    // Módulo de nueva nómina
    'nuevanomina': () => import(/* webpackChunkName: "nominas" */ './pages/nuevanomina.js'),

    // Módulo de bienvenida/onboarding
    'welcome': () => import(/* webpackChunkName: "welcome" */ './pages/welcome.js'),

    // Portal de desarrolladores (unifica Apps Connect, API Keys y Apps conectadas)
    'settings-developers': () => import(/* webpackChunkName: "settings" */ './pages/settings-developers.js'),
    'settings_developers': () => import(/* webpackChunkName: "settings" */ './pages/settings-developers.js'),

    // Módulo de módulos
    'settings-modules': () => import(/* webpackChunkName: "settings" */ './pages/settings-modules.js'),
    'settings_modules': () => import(/* webpackChunkName: "settings" */ './pages/settings-modules.js'),

    // Módulo de lugares
    'settings-places': () => import(/* webpackChunkName: "settings" */ './pages/settings-places.js'),
    'settings_places': () => import(/* webpackChunkName: "settings" */ './pages/settings-places.js'),

    // Módulo de configuración de exportadores
    'settings-export': () => import(/* webpackChunkName: "settings" */ './pages/settings-export.js'),
    'settings_export': () => import(/* webpackChunkName: "settings" */ './pages/settings-export.js'),

    // Módulo de certificado electrónico
    'settings-electronic-certificate': () => import(/* webpackChunkName: "settings" */ './pages/settings-electronic-certificate.js'),
    'settings_electronic-certificate': () => import(/* webpackChunkName: "settings" */ './pages/settings-electronic-certificate.js'),

    // Módulo de settings index
    'settings-index': () => import(/* webpackChunkName: "settings" */ './pages/settings-index.js'),
    'settings_index': () => import(/* webpackChunkName: "settings" */ './pages/settings-index.js'),

    // Módulo de settings docs (actividad / copias de seguridad)
    'settings-docs': () => import(/* webpackChunkName: "settings-docs" */ './pages/settings-docs.js'),
    'settings_docs': () => import(/* webpackChunkName: "settings-docs" */ './pages/settings-docs.js'),
    'contabilidad-periodos': () => import(/* webpackChunkName: "contabilidad-periodos" */ './pages/contabilidad-periodos.js'),
    'contabilidad_periodos': () => import(/* webpackChunkName: "contabilidad-periodos" */ './pages/contabilidad-periodos.js'),
    'contabilidad-configuracion-contactos': () => import(/* webpackChunkName: "contabilidad-configuracion-contactos" */ './pages/contabilidad-configuracion-contactos.js'),
    'contabilidad_configuracion-contactos': () => import(/* webpackChunkName: "contabilidad-configuracion-contactos" */ './pages/contabilidad-configuracion-contactos.js'),

    // Módulo de gestión contable
    'gestion-contable': () => import(/* webpackChunkName: "gestion-contable" */ './pages/gestion-contable.js'),
    'gestion_contable': () => import(/* webpackChunkName: "gestion-contable" */ './pages/gestion-contable.js'),
    'gestion-contable-periodo': () => import(/* webpackChunkName: "gestion-contable" */ './pages/gestion-contable-periodo.js'),
    'gestion_contable_periodo': () => import(/* webpackChunkName: "gestion-contable" */ './pages/gestion-contable-periodo.js'),

    // Módulo de nuevo empleado
    'nuevoempleado': () => import(/* webpackChunkName: "contactos" */ './pages/nuevoempleado.js'),

    // Módulo de ayuda
    'help': () => import(/* webpackChunkName: "settings" */ './pages/help.js'),

    // Módulo de perfil de negocio
    'business-profile': () => import(/* webpackChunkName: "partners" */ './pages/business-profile.js'),

    // Módulo de modelos tributarios
    'taxmodels': () => import(/* webpackChunkName: "taxmodels" */ './pages/taxmodels.js'),

    // Módulo de Modelo 347
    'modelo347': () => import(/* webpackChunkName: "modelo347" */ './pages/modelo347.js'),

    // Módulo de TPV (Punto de Venta)
    'tpv': () => import(/* webpackChunkName: "tpv" */ './pages/tpv.js'),

    // Módulo de soporte
    'support': () => import(/* webpackChunkName: "support" */ './pages/support.js'),

    // Módulo de tickets de soporte
    'support-tickets': () => import(/* webpackChunkName: "support-tickets" */ './pages/support-tickets.js'),
    'support_tickets': () => import(/* webpackChunkName: "support-tickets" */ './pages/support-tickets.js'),
    'support-ticket': () => import(/* webpackChunkName: "support-ticket" */ './pages/support-ticket.js'),
    'support_ticket': () => import(/* webpackChunkName: "support-ticket" */ './pages/support-ticket.js'),

    // Módulo de admin
    'admin': () => import(/* webpackChunkName: "admin" */ './pages/admin.js'),
    'adminKg6qCxgX29xs': () => import(/* webpackChunkName: "admin" */ './pages/admin.js'),

    // Módulo de form_presupuesto (compartido por presupuestos, proformas, rectificativas)
    'presupuestos-form_presupuesto': () => import(/* webpackChunkName: "form-presupuesto" */ './pages/form-presupuesto.js'),
    'presupuestos-form-presupuesto': () => import(/* webpackChunkName: "form-presupuesto" */ './pages/form-presupuesto.js'),
    'proformas-form_presupuesto': () => import(/* webpackChunkName: "form-presupuesto" */ './pages/form-presupuesto.js'),
    'proformas-form-presupuesto': () => import(/* webpackChunkName: "form-presupuesto" */ './pages/form-presupuesto.js'),
    'rectificativas-form_presupuesto': () => import(/* webpackChunkName: "form-presupuesto" */ './pages/form-presupuesto.js'),
    'rectificativas-form-presupuesto': () => import(/* webpackChunkName: "form-presupuesto" */ './pages/form-presupuesto.js'),

    // Módulo de importación de facturas
    'facturas-import': () => import(/* webpackChunkName: "facturas-import" */ './pages/facturas-import.js'),
    'facturas_import': () => import(/* webpackChunkName: "facturas-import" */ './pages/facturas-import.js'),

    // Módulo de importación de gastos
    'gastos-import': () => import(/* webpackChunkName: "gastos-import" */ './pages/gastos-import.js'),
    'gastos_import': () => import(/* webpackChunkName: "gastos-import" */ './pages/gastos-import.js'),


    // Módulo de importación de albaranes
    'albaranes-import': () => import(/* webpackChunkName: "albaranes-import" */ './pages/albaranes-import.js'),
    'albaranes_import': () => import(/* webpackChunkName: "albaranes-import" */ './pages/albaranes-import.js'),

    // Módulo de importación de presupuestos
    'presupuestos-import': () => import(/* webpackChunkName: "presupuestos-import" */ './pages/presupuestos-import.js'),
    'presupuestos_import': () => import(/* webpackChunkName: "presupuestos-import" */ './pages/presupuestos-import.js'),

    // Módulo de importación de proformas
    'proformas-import': () => import(/* webpackChunkName: "proformas-import" */ './pages/proformas-import.js'),
    'proformas_import': () => import(/* webpackChunkName: "proformas-import" */ './pages/proformas-import.js'),

    // Módulo de historial de importaciones
    'importaciones-historial': () => import(/* webpackChunkName: "importaciones-historial" */ './pages/importaciones-historial.js'),

    // Módulo de gastos por email
    'gastos-email': () => import(/* webpackChunkName: "gastos" */ './pages/gastos-email.js'),
    'gastos_email': () => import(/* webpackChunkName: "gastos" */ './pages/gastos-email.js'),

    // Módulo de fichajes de equipo
    'fichajes-equipo': () => import(/* webpackChunkName: "fichajes" */ './pages/fichajes-equipo.js'),
    'fichajes_equipo': () => import(/* webpackChunkName: "fichajes" */ './pages/fichajes-equipo.js'),

    // Módulo de remesas item
    'remesas-item': () => import(/* webpackChunkName: "remesas" */ './pages/remesas-item.js'),
    'remesas_item': () => import(/* webpackChunkName: "remesas" */ './pages/remesas-item.js'),

    // Módulo de proformas
    'proformas': () => import(/* webpackChunkName: "proformas" */ './pages/proformas.js'),

    // Módulo de contactos single
    'contactos-single': () => import(/* webpackChunkName: "contactos" */ './pages/contactos-single.js'),
    'contactos_single': () => import(/* webpackChunkName: "contactos" */ './pages/contactos-single.js'),
    'contact-contactos-single': () => import(/* webpackChunkName: "contactos" */ './pages/contactos-single.js'),
    'contact-contactos_single': () => import(/* webpackChunkName: "contactos" */ './pages/contactos-single.js'),

    // Módulo de importación de contactos
    'contactos-import': () => import(/* webpackChunkName: "contactos" */ './pages/contactos-import.js'),
    'contactos_import': () => import(/* webpackChunkName: "contactos" */ './pages/contactos-import.js'),

    // Módulo de empresa (configuración de empresa)
    'empresa': () => import(/* webpackChunkName: "empresa" */ './pages/empresa.js'),

    // Módulo de impuestos simple (Impuestos del menú principal)
    'taxs': () => import(/* webpackChunkName: "taxs" */ './pages/taxs.js'),

    // Módulo de impuestos avanzado (oculto bajo Contabilidad)
    'contabilidad-impuestos': () => import(/* webpackChunkName: "contabilidad-impuestos" */ './pages/contabilidad-impuestos.js'),
    'contabilidad_impuestos': () => import(/* webpackChunkName: "contabilidad-impuestos" */ './pages/contabilidad-impuestos.js'),
    'contabilidad-impuestos-calendar': () => import(/* webpackChunkName: "contabilidad-impuestos" */ './pages/contabilidad-impuestos-calendar.js'),
    'contabilidad_impuestos_calendar': () => import(/* webpackChunkName: "contabilidad-impuestos" */ './pages/contabilidad-impuestos-calendar.js'),
    'contabilidad-impuestos-declaration': () => import(/* webpackChunkName: "contabilidad-impuestos" */ './pages/contabilidad-impuestos-declaration.js'),
    'contabilidad_impuestos_declaration': () => import(/* webpackChunkName: "contabilidad-impuestos" */ './pages/contabilidad-impuestos-declaration.js'),

    // Módulo de programa de referidos
    'referral-program': () => import(/* webpackChunkName: "referral-program" */ './pages/referral-program.js'),
    'referral_program': () => import(/* webpackChunkName: "referral-program" */ './pages/referral-program.js'),

    // Módulo de separador de PDFs
    'cropfiles': () => import(/* webpackChunkName: "cropfiles" */ './pages/cropfiles.js'),

    // Módulo de edición de usuario
    'settings-user': () => import(/* webpackChunkName: "settings" */ './pages/settings-user.js'),
    'settings_user': () => import(/* webpackChunkName: "settings" */ './pages/settings-user.js'),

    // Módulo de cambio de cuenta
    'settings-changeaccount': () => import(/* webpackChunkName: "settings" */ './pages/settings-changeaccount.js'),
    'settings_changeaccount': () => import(/* webpackChunkName: "settings" */ './pages/settings-changeaccount.js'),

    // Módulos de series de numeración
    'settings-serials-new': () => import(/* webpackChunkName: "settings" */ './pages/settings-serials-new.js'),
    'settings_serials_new': () => import(/* webpackChunkName: "settings" */ './pages/settings-serials-new.js'),
    'settings-serials-ed': () => import(/* webpackChunkName: "settings" */ './pages/settings-serials-ed.js'),
    'settings_serials_ed': () => import(/* webpackChunkName: "settings" */ './pages/settings-serials-ed.js'),

    // Módulos de impuestos (crear/editar)
    'settings-preferences-new': () => import(/* webpackChunkName: "settings" */ './pages/settings-preferences-new.js'),
    'settings_preferences_new': () => import(/* webpackChunkName: "settings" */ './pages/settings-preferences-new.js'),
    'settings-preferences-ed': () => import(/* webpackChunkName: "settings" */ './pages/settings-preferences-ed.js'),
    'settings_preferences_ed': () => import(/* webpackChunkName: "settings" */ './pages/settings-preferences-ed.js'),

    // Módulo de eliminación de documentos
    'documento-del': () => import(/* webpackChunkName: "documento" */ './pages/documento-del.js'),
    'documento_del': () => import(/* webpackChunkName: "documento" */ './pages/documento-del.js'),

    // Módulo de referidos registrados (compartido por programa estándar y partners)
    'referral-program-registered': () => import(/* webpackChunkName: "referral-program" */ './pages/referral-program-registered.js'),
    'referral_program_registered': () => import(/* webpackChunkName: "referral-program" */ './pages/referral-program-registered.js'),
    'partners-referral-program-registered': () => import(/* webpackChunkName: "referral-program" */ './pages/referral-program-registered.js').then(m => ({ initPartnersReferralProgramRegistered: m.initReferralProgramRegistered })),
    'partners_referral_program_registered': () => import(/* webpackChunkName: "referral-program" */ './pages/referral-program-registered.js').then(m => ({ initPartnersReferralProgramRegistered: m.initReferralProgramRegistered })),

    // Documentos sent/received
    'documents-sent': () => import(/* webpackChunkName: "documents" */ './pages/documents-sent.js'),
    'documents_sent': () => import(/* webpackChunkName: "documents" */ './pages/documents-sent.js'),
    'documents-sent-item': () => import(/* webpackChunkName: "documents" */ './pages/documents-sent_item.js'),
    'documents_sent_item': () => import(/* webpackChunkName: "documents" */ './pages/documents-sent_item.js'),
    'documents-received': () => import(/* webpackChunkName: "documents" */ './pages/documents-received.js'),
    'documents_received': () => import(/* webpackChunkName: "documents" */ './pages/documents-received.js'),
    'documents-received-item': () => import(/* webpackChunkName: "documents" */ './pages/documents-received_item.js'),
    'documents_received_item': () => import(/* webpackChunkName: "documents" */ './pages/documents-received_item.js'),

    // Módulo de ausencias del equipo
    'ausencias-equipo': () => import(/* webpackChunkName: "ausencias" */ './pages/ausencias-equipo.js'),
    'ausencias_equipo': () => import(/* webpackChunkName: "ausencias" */ './pages/ausencias-equipo.js'),

    // Módulo de permisos de usuario
    'settings-permissions': () => import(/* webpackChunkName: "settings" */ './pages/settings-permissions.js'),
    'settings_permissions': () => import(/* webpackChunkName: "settings" */ './pages/settings-permissions.js'),

    // Módulo de gestión de dispositivos (ahora integrado en settings-security)
    'settings-devices': () => import(/* webpackChunkName: "settings" */ './pages/settings-security.js'),
    'settings_devices': () => import(/* webpackChunkName: "settings" */ './pages/settings-security.js'),

    // Módulo de pedidos
    'pedidos': () => import(/* webpackChunkName: "pedidos" */ './pages/pedidos.js'),

    // Módulo de eliminación de gastos
    'gastos-del': () => import(/* webpackChunkName: "gastos" */ './pages/gastos-del.js'),
    'gastos_del': () => import(/* webpackChunkName: "gastos" */ './pages/gastos-del.js'),

    // Módulos de productos (form/edit)
    'producto-form': () => import(/* webpackChunkName: "productos-forms" */ './pages/producto-form.js'),
    'producto_form': () => import(/* webpackChunkName: "productos-forms" */ './pages/producto-form.js'),
    'producto-edit-form': () => import(/* webpackChunkName: "productos-forms" */ './pages/producto-edit-form.js'),
    'producto_edit_form': () => import(/* webpackChunkName: "productos-forms" */ './pages/producto-edit-form.js'),
    'producto-edit': () => import(/* webpackChunkName: "productos-forms" */ './pages/producto-edit-form.js'),
    'producto_edit': () => import(/* webpackChunkName: "productos-forms" */ './pages/producto-edit-form.js'),

    // Módulos de configuración de seriales
    'configuration-seriales-form': () => import(/* webpackChunkName: "configuration" */ './pages/configuration-seriales-form.js'),
    'configuration_seriales_form': () => import(/* webpackChunkName: "configuration" */ './pages/configuration-seriales-form.js'),
    'configuration-seriales-edit': () => import(/* webpackChunkName: "configuration" */ './pages/configuration-seriales-edit.js'),
    'configuration_seriales_edit': () => import(/* webpackChunkName: "configuration" */ './pages/configuration-seriales-edit.js'),
    'configuration-seriales-del': () => import(/* webpackChunkName: "configuration" */ './pages/configuration-seriales-del.js'),
    'configuration_seriales_del': () => import(/* webpackChunkName: "configuration" */ './pages/configuration-seriales-del.js'),

    // Módulos de configuración de impuestos
    'configuration-tax-form': () => import(/* webpackChunkName: "configuration" */ './pages/configuration-tax-form.js'),
    'configuration_tax_form': () => import(/* webpackChunkName: "configuration" */ './pages/configuration-tax-form.js'),
    'configuration-tax-edit': () => import(/* webpackChunkName: "configuration" */ './pages/configuration-tax-edit.js'),
    'configuration_tax_edit': () => import(/* webpackChunkName: "configuration" */ './pages/configuration-tax-edit.js'),
    'configuration-tax-del': () => import(/* webpackChunkName: "configuration" */ './pages/configuration-tax-del.js'),
    'configuration_tax_del': () => import(/* webpackChunkName: "configuration" */ './pages/configuration-tax-del.js'),

    // Módulos de clientes (CRUD)
    'clientes-del': () => import(/* webpackChunkName: "clientes" */ './pages/clientes-del.js'),
    'clientes_del': () => import(/* webpackChunkName: "clientes" */ './pages/clientes-del.js'),
    'clientes-form': () => import(/* webpackChunkName: "clientes" */ './pages/clientes-form.js'),
    'clientes_form': () => import(/* webpackChunkName: "clientes" */ './pages/clientes-form.js'),
    'clientes-edit': () => import(/* webpackChunkName: "clientes" */ './pages/clientes-edit.js'),
    'clientes_edit': () => import(/* webpackChunkName: "clientes" */ './pages/clientes-edit.js'),

    // Módulos de permisos (crear/editar)
    'settings-permissions-ed': () => import(/* webpackChunkName: "settings" */ './pages/settings-permissions-ed.js'),
    'settings_permissions_ed': () => import(/* webpackChunkName: "settings" */ './pages/settings-permissions-ed.js'),
    'settings-permissions-new': () => import(/* webpackChunkName: "settings" */ './pages/settings-permissions-new.js'),
    'settings_permissions_new': () => import(/* webpackChunkName: "settings" */ './pages/settings-permissions-new.js'),

    // Módulo de subida de archivos
    'uploads-new': () => import(/* webpackChunkName: "uploads" */ './pages/uploads-new.js'),
    'uploads_new': () => import(/* webpackChunkName: "uploads" */ './pages/uploads-new.js'),

    // Asesoría (cliente vinculado a asesor)
    'asesoria-recibidos': () => import(/* webpackChunkName: "asesoria" */ './pages/asesoria-recibidos.js'),
    'asesoria_recibidos': () => import(/* webpackChunkName: "asesoria" */ './pages/asesoria-recibidos.js'),
    'asesoria-recibidos-item': () => import(/* webpackChunkName: "asesoria" */ './pages/asesoria-recibidos_item.js'),
    'asesoria_recibidos_item': () => import(/* webpackChunkName: "asesoria" */ './pages/asesoria-recibidos_item.js'),
    'asesoria-enviados': () => import(/* webpackChunkName: "asesoria" */ './pages/asesoria-enviados.js'),
    'asesoria_enviados': () => import(/* webpackChunkName: "asesoria" */ './pages/asesoria-enviados.js'),
    'asesoria-enviados-item': () => import(/* webpackChunkName: "asesoria" */ './pages/asesoria-enviados_item.js'),
    'asesoria_enviados_item': () => import(/* webpackChunkName: "asesoria" */ './pages/asesoria-enviados_item.js'),
    'asesoria-subir-new': () => import(/* webpackChunkName: "uploads" */ './pages/asesoria-subir-new.js'),
    'asesoria_subir_new': () => import(/* webpackChunkName: "uploads" */ './pages/asesoria-subir-new.js'),

    // Módulo de programa de referidos para partners
    'partners-referral-program': () => import(/* webpackChunkName: "partners" */ './pages/partners-referral-program.js'),
    'partners_referral_program': () => import(/* webpackChunkName: "partners" */ './pages/partners-referral-program.js'),

    // Módulo de eliminación de presupuestos/proformas/rectificativas
    'presupuestos-del_presupuesto': () => import(/* webpackChunkName: "del-presupuesto" */ './pages/del-presupuesto.js'),
    'presupuestos-del-presupuesto': () => import(/* webpackChunkName: "del-presupuesto" */ './pages/del-presupuesto.js'),
    'proformas-del_presupuesto': () => import(/* webpackChunkName: "del-presupuesto" */ './pages/del-presupuesto.js'),
    'proformas-del-presupuesto': () => import(/* webpackChunkName: "del-presupuesto" */ './pages/del-presupuesto.js'),
    'rectificativas-del_presupuesto': () => import(/* webpackChunkName: "del-presupuesto" */ './pages/del-presupuesto.js'),
    'rectificativas-del-presupuesto': () => import(/* webpackChunkName: "del-presupuesto" */ './pages/del-presupuesto.js'),

    // Módulo de eliminación de productos
    'user-del_producto': () => import(/* webpackChunkName: "productos-forms" */ './pages/user-del-producto.js'),
    'user-del-producto': () => import(/* webpackChunkName: "productos-forms" */ './pages/user-del-producto.js'),

    // Módulo de edición de productos (user)
    'user-edit_form_producto': () => import(/* webpackChunkName: "productos-forms" */ './pages/user-edit-form-producto.js'),
    'user-edit-form-producto': () => import(/* webpackChunkName: "productos-forms" */ './pages/user-edit-form-producto.js'),

    // Módulo de listado de clientes (subdirectorio)
    'clientes-index': () => import(/* webpackChunkName: "clientes" */ './pages/clientes-index.js'),

    // Módulo de contactos single (subdirectorio clientes)
    'clientes-contactos_single': () => import(/* webpackChunkName: "contactos" */ './pages/clientes-contactos-single.js'),
    'clientes-contactos-single': () => import(/* webpackChunkName: "contactos" */ './pages/clientes-contactos-single.js'),

    // Módulo de integración Shopify
    'settings-shopify': () => import(/* webpackChunkName: "settings" */ './pages/settings-shopify.js'),
    'settings_shopify': () => import(/* webpackChunkName: "settings" */ './pages/settings-shopify.js'),

    // Módulo de recuperación de contraseña (reset)
    'passwordrecovery': () => import(/* webpackChunkName: "auth" */ './pages/passwordrecovery.js'),
    'passwordrecovery-index': () => import(/* webpackChunkName: "auth" */ './pages/passwordrecovery.js'),

    // Módulo de eliminación de proveedores
    'proveedores-del': () => import(/* webpackChunkName: "proveedores" */ './pages/proveedores-del.js'),
    'proveedores_del': () => import(/* webpackChunkName: "proveedores" */ './pages/proveedores-del.js'),

    // Módulo de eliminación de impuestos (settings)
    'settings-preferences-del': () => import(/* webpackChunkName: "settings" */ './pages/settings-preferences-del.js'),
    'settings_preferences_del': () => import(/* webpackChunkName: "settings" */ './pages/settings-preferences-del.js'),

    // Módulo de eliminación de seriales (settings)
    'settings-serials-del': () => import(/* webpackChunkName: "settings" */ './pages/settings-serials-del.js'),
    'settings_serials_del': () => import(/* webpackChunkName: "settings" */ './pages/settings-serials-del.js'),

    // Módulo de eliminación de productos
    'productos-del': () => import(/* webpackChunkName: "productos-forms" */ './pages/productos-del.js'),
    'productos_del': () => import(/* webpackChunkName: "productos-forms" */ './pages/productos-del.js'),

    // Módulo de exportación de documentos (modal)
    'export-documents': () => import(/* webpackChunkName: "export-documents" */ './pages/export-documents.js'),

    // Módulo de exportación de productos (modal)
    'export-products': () => import(/* webpackChunkName: "export-products" */ './pages/export-products.js'),

    // Módulo de exportación de remesas (modal)
    'export-remittance': () => import(/* webpackChunkName: "export-remittance" */ './pages/export-remittance.js'),

    // Módulo de transacciones bancarias
    'transacciones': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones.js'),
    'transacciones-index': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones.js'),
    'transacciones-reglas': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones-reglas.js'),
    'transacciones_reglas': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones-reglas.js'),
    'transacciones-detalle': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones-detalle.js'),
    'transacciones_detalle': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones-detalle.js'),
    'transacciones-import': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones-import.js'),
    'transacciones_import': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones-import.js'),
    'transacciones-conciliacion': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones-conciliacion.js'),
    'transacciones_conciliacion': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones-conciliacion.js'),

    // Módulo de banca (nueva UI /banking/*)
    'banking': () => import(/* webpackChunkName: "banking" */ './pages/banking.js'),
    'banking-index': () => import(/* webpackChunkName: "banking" */ './pages/banking.js'),
    'banking-cuenta': () => import(/* webpackChunkName: "banking" */ './pages/banking-cuenta.js'),
    'banking_cuenta': () => import(/* webpackChunkName: "banking" */ './pages/banking-cuenta.js'),
    // Shadows que reutilizan el módulo JS legacy de /transacciones
    // Re-exportamos el init con el nombre que espera pageLoader (initBankingReglas, etc.)
    'banking-reglas': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones-reglas.js').then(m => ({ initBankingReglas: m.initTransaccionesReglas })),
    'banking_reglas': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones-reglas.js').then(m => ({ initBankingReglas: m.initTransaccionesReglas })),
    'banking-import': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones-import.js').then(m => ({ initBankingImport: m.initTransaccionesImport })),
    'banking_import': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones-import.js').then(m => ({ initBankingImport: m.initTransaccionesImport })),
    'banking-cuenta-conciliacion': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones-conciliacion.js').then(m => ({ initBankingCuentaConciliacion: m.initTransaccionesConciliacion })),
    'banking_cuenta_conciliacion': () => import(/* webpackChunkName: "transacciones" */ './pages/transacciones-conciliacion.js').then(m => ({ initBankingCuentaConciliacion: m.initTransaccionesConciliacion })),

    // Módulo de inventario
    'inventario': () => import(/* webpackChunkName: "inventario" */ './pages/inventario.js'),
    'inventario-producto': () => import(/* webpackChunkName: "inventario" */ './pages/inventario-producto.js'),
    'inventario_producto': () => import(/* webpackChunkName: "inventario" */ './pages/inventario-producto.js'),
    'inventario-movimiento': () => import(/* webpackChunkName: "inventario" */ './pages/inventario-movimiento.js'),
    'inventario_movimiento': () => import(/* webpackChunkName: "inventario" */ './pages/inventario-movimiento.js'),
};

// Registro de módulos ya inicializados en esta sesión de página
let initializedModules = new Set();

// Registro de funciones de página asignadas a window (para cleanup al navegar)
const registeredPageFunctions = new Set();
// Registro de intervalos y timeouts activos
const activeIntervals = new Set();
const activeTimeouts = new Set();
// Registro de event listeners activos
const activeListeners = new Set();

// Referencias originales a funciones nativas
// Se definen fuera para que estén disponibles en el scope del módulo
const originalSetInterval = window.setInterval;
const originalClearInterval = window.clearInterval;
const originalSetTimeout = window.setTimeout;
const originalClearTimeout = window.clearTimeout;
const nativeAddEventListener = EventTarget.prototype.addEventListener;
const nativeRemoveEventListener = EventTarget.prototype.removeEventListener;

/**
 * Habilita el seguimiento de recursos (Intervals, Timeouts, EventListeners).
 * Se debe llamar UNA SOLA VEZ al finalizar la inicialización del sistema.
 */
function enableResourceTracking() {
    // 1. Interceptar setInterval
    window.setInterval = function (handler, timeout, ...args) {
        const id = originalSetInterval(handler, timeout, ...args);
        activeIntervals.add(id);
        return id;
    };

    window.clearInterval = function (id) {
        activeIntervals.delete(id);
        originalClearInterval(id);
    };

    // 2. Interceptar setTimeout
    window.setTimeout = function (handler, timeout, ...args) {
        const id = originalSetTimeout(handler, timeout, ...args);
        activeTimeouts.add(id);
        return id;
    };

    window.clearTimeout = function (id) {
        activeTimeouts.delete(id);
        originalClearTimeout(id);
    };

    // 3. Interceptar addEventListener (SOLO para window y document)
    EventTarget.prototype.addEventListener = function (type, listener, options) {
        nativeAddEventListener.call(this, type, listener, options);

        if (this === window || this === document || (document.body && this === document.body)) {
            activeListeners.add({ target: this, type, listener, options });
        }
    };

    EventTarget.prototype.removeEventListener = function (type, listener, options) {
        nativeRemoveEventListener.call(this, type, listener, options);

        for (const item of activeListeners) {
            if (item.target === this && item.type === type && item.listener === listener) {
                activeListeners.delete(item);
                break;
            }
        }
    };
}

/**
 * Limpia todos los recursos registrados (timers, listeners) y propiedades globales.
 */
function cleanupPageResources() {
    // 1. Limpiar Intervalos
    activeIntervals.forEach(id => originalClearInterval(id));
    activeIntervals.clear();

    // 2. Limpiar Timeouts
    activeTimeouts.forEach(id => originalClearTimeout(id));
    activeTimeouts.clear();

    // 3. Limpiar Event Listeners rastreados
    activeListeners.forEach(({ target, type, listener, options }) => {
        nativeRemoveEventListener.call(target, type, listener, options);
    });
    activeListeners.clear();

    // 4. Limpiar propiedades de eventos globales
    const propertiesToClear = [
        'onclick', 'ondblclick', 'onmousedown', 'onmouseup', 'onmousemove',
        'onmouseover', 'onmouseout', 'onmouseenter', 'onmouseleave',
        'onkeydown', 'onkeypress', 'onkeyup',
        'onchange', 'oninput', 'onsubmit', 'onreset', 'onselect', 'onscroll', 'onresize',
        'onload', 'onunload', 'onbeforeunload'
    ];

    propertiesToClear.forEach(prop => {
        if (window[prop] !== null && window[prop] !== undefined) window[prop] = null;
        if (document[prop] !== null && document[prop] !== undefined) document[prop] = null;
        if (document.body && document.body[prop] !== null && document.body[prop] !== undefined) document.body[prop] = null;
    });
}

// setupSlider() appends `<sliderId>_background` divs to <body>, outside the
// turbo-frame, so they survive Turbo navigation. Without cleanup, the old
// slider overlay covers the new page. Remove them — setupSlider will recreate
// on demand (see bundle.js: `if (!slidingBackground)`).
function cleanupOrphanSliderBackgrounds() {
    const bgs = document.querySelectorAll(
        'body > div[id^="slidingPanel"][id$="_background"], ' +
        'body > div[id^="slidingFilter"][id$="_background"]'
    );
    bgs.forEach(bg => bg.remove());

    document.body.classList.remove('overflow-hidden');
}

// Página actualmente cargada (para evitar recargas duplicadas entre eventos Turbo)
let _currentPage = null;

// Página que está en proceso de carga (para evitar cargas concurrentes)
let _loadingPage = null;

// Generation counter: incremented each time a new navigation triggers module
// loading. Stale async inits (from a previous generation) are discarded so
// they don't attach handlers to DOM nodes that Turbo has already replaced.
let _loadGeneration = 0;

// URL of the last handled navigation. Used to deduplicate when multiple
// Turbo events fire for the same navigation (turbo:frame-load + turbo:render).
// Reset to null on turbo:before-fetch-request so after a fetch completes
// the next handler re-initializes. For back/forward (cache restoration),
// no fetch fires, but the URL changes, so the dedup naturally allows reinit.
let _lastHandledUrl = null;

// Flag that prevents turbo:render / turbo:load from calling loadPageModule
// while a Turbo fetch is in progress.  During frame-based navigation the
// events fire BEFORE turbo:frame-load replaces the DOM, so running init
// functions at that point attaches handlers to stale (soon-to-be-replaced)
// elements.  Only turbo:frame-load (which fires AFTER the DOM is updated)
// should trigger module loading when a fetch is pending.
let _fetchPending = false;

/**
 * Registra una función en window y la añade al set de limpieza.
 * Usar esto en lugar de window[name] = fn directamente para que se
 * limpie automáticamente al navegar con Turbo.
 *
 * @param {string} name - Nombre de la propiedad en window
 * @param {Function} fn - Función a registrar
 */
function registerPageFunction(name, fn) {
    window[name] = fn;
    registeredPageFunctions.add(name);
}

/**
 * Elimina todas las funciones de página registradas de window.
 * Se ejecuta antes de cada navegación Turbo para evitar
 * memory leaks y colisiones de nombres entre páginas.
 */
function cleanupPageFunctions() {
    registeredPageFunctions.forEach(function (name) {
        delete window[name];
    });
    registeredPageFunctions.clear();
    cleanupPageResources();
}

/**
 * Convierte un nombre de página a nombre de función init
 * Ej: "documento-nuevo" -> "initDocumentoNuevo"
 */
function toInitFunctionName(pageName) {
    const parts = pageName.split(/[-_]/);
    const camelCase = parts.map((part) => {
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    }).join('');
    return 'init' + camelCase;
}

/**
 * Carga e inicializa un módulo de página
 * @param {string} pageName - Nombre de la página (del atributo data-page)
 * @param {boolean} force - Forzar reinicialización aunque ya se haya cargado
 */
async function loadPageModule(pageName, force = false) {
    if (!pageName) return;

    // Evitar reinicialización duplicada (a menos que se fuerce)
    if (!force && (initializedModules.has(pageName) || _loadingPage === pageName)) {
        return;
    }

    const loader = pageModules[pageName];
    if (!loader) {
        return;
    }

    // Marcar como en proceso de carga para evitar cargas concurrentes
    _loadingPage = pageName;
    var myGeneration = _loadGeneration;

    try {
        const module = await loader();

        // Minimal yield: setTimeout(0) ensures any pending DOM work is
        // flushed (~1-4ms) without the long rAF delay (~16ms) that caused
        // a visible window where cached pages had no click handlers.
        await new Promise(resolve => setTimeout(resolve, 0));

        // If a newer navigation started while we were loading, discard this
        // init — the DOM nodes we'd attach to may have been replaced by Turbo.
        if (myGeneration !== _loadGeneration) {
            return;
        }

        // Si la página cambió mientras cargábamos, no inicializar
        // (skip this check when force=true, e.g. modal-loaded modules)
        if (!force && _currentPage !== null && _currentPage !== pageName) {
            return;
        }

        // Intentar llamar a la función init correspondiente
        const initFnName = toInitFunctionName(pageName);

        if (typeof module[initFnName] === 'function') {
            module[initFnName]();
            initializedModules.add(pageName);
        } else if (typeof module.default === 'function') {
            module.default();
            initializedModules.add(pageName);
        } else {
            const exportedFns = Object.values(module).filter(v => typeof v === 'function');
            if (exportedFns.length > 0) {
                exportedFns[0]();
                initializedModules.add(pageName);
            }
        }
    } catch (error) {
        console.error(`[PageLoader] Error cargando módulo para página "${pageName}":`, error);
    } finally {
        if (_loadingPage === pageName) {
            _loadingPage = null;
        }
    }
}

/**
 * Detecta la página actual desde el atributo data-page
 * Prioriza el contenido del turbo-frame para navegación SPA
 */
function detectCurrentPage() {
    // Prioridad 1: buscar data-page dentro del turbo-frame (contenido cargado dinámicamente)
    // Esto es necesario porque el body.dataset.page no cambia con navegación Turbo
    const turboFrame = document.getElementById('main');
    if (turboFrame) {
        // Buscar el primer elemento hijo directo con data-page
        const pageElement = turboFrame.querySelector('[data-page]');
        if (pageElement && pageElement.dataset.page) {
            return pageElement.dataset.page;
        }
    }

    // Prioridad 2: detectar desde URL (más confiable que body para navegación Turbo)
    const urlPage = detectPageFromUrl();
    if (urlPage) {
        return urlPage;
    }

    // Prioridad 3: atributo data-page en el body (carga inicial)
    const body = document.body;
    if (body && body.dataset.page) {
        return body.dataset.page;
    }

    return null;
}

/**
 * Detecta la página desde la URL actual
 * Usa la misma lógica que header.php para consistencia
 */
function detectPageFromUrl() {
    const path = window.location.pathname;
    const search = window.location.search;

    // Caso especial: home
    if (path === '/' || path === '/index.php') return 'home';

    // Extraer segmentos del path (igual que PHP)
    const segments = path.split('/').filter(Boolean);

    if (segments.length === 0) return null;

    // Rutas dinámicas con ID en medio o al final.
    // /banking/cuenta/{id}                -> banking-cuenta
    // /banking/cuenta/{id}/conciliacion   -> banking-cuenta-conciliacion
    if (segments[0] === 'banking' && segments[1] === 'cuenta' && segments[2]) {
        return segments[3] === 'conciliacion' ? 'banking-cuenta-conciliacion' : 'banking-cuenta';
    }

    // Combinar segmentos con guión (ej: gastos/reader -> gastos-reader, settings/preferences/ed -> settings-preferences-ed)
    if (segments.length >= 2) {
        return segments.join('-');
    }

    // Un solo segmento — return it directly (query string checks are only a fallback for root path)
    if (segments.length === 1) {
        return segments[0];
    }

    // Casos especiales por query string (para compatibilidad con root path)
    if (search.includes('doc=factura')) return 'facturas';
    if (search.includes('doc=presupuesto')) return 'presupuestos';
    if (search.includes('doc=gasto')) return 'gastos';
}

/**
 * Inicializa el cargador de páginas
 * Se debe llamar una vez al cargar la aplicación
 */
function initPageLoader() {
    // Shared handler for turbo:frame-load, turbo:render and turbo:load.
    // Deduplicates using URL: when multiple events fire for the same
    // navigation (e.g. frame-load + render), only the first one runs.
    // turbo:before-fetch-request resets _lastHandledUrl so after a fetch
    // the next event re-initializes. For back/forward (cache restoration),
    // no fetch fires but the URL changes, so the handler runs naturally.
    function handleNavigation(opts) {
        var currentUrl = window.location.href;
        if (currentUrl === _lastHandledUrl) return;
        _lastHandledUrl = currentUrl;

        cleanupPageFunctions();

        // Invalidate any in-progress async module load from a previous
        // navigation (or from tryInitialLoad before Turbo replaced the DOM).
        _loadingPage = null;
        _loadGeneration++;

        // Synchronously initialize Flowbite dropdowns, calendars, sliders, etc.
        // BEFORE the async module load. This closes the ~20-50ms gap where the
        // DOM is rendered but click handlers (dropdowns, etc.) aren't attached.
        if (typeof Facturavia !== 'undefined' && Facturavia.preload) {
            Facturavia.preload();
        }

        var newPage = detectCurrentPage();
        _currentPage = newPage;
        initializedModules.clear();

        // When a Turbo fetch is in progress (frame navigation), defer module
        // loading to turbo:frame-load which fires AFTER the DOM is updated.
        // turbo:render / turbo:load may fire before the frame content is
        // replaced, causing init functions to run against stale DOM.
        // The caller can bypass this guard (e.g. turbo:frame-load itself).
        if (_fetchPending && !(opts && opts.domReady)) return;

        loadPageModule(newPage);
    }

    // On fetch-based navigation, reset _lastHandledUrl so the next
    // frame-load/render event re-initializes the module.
    // Also set _fetchPending so turbo:render / turbo:load defer module
    // loading to turbo:frame-load (which guarantees the DOM is ready).
    document.addEventListener('turbo:before-fetch-request', function () {
        cleanupPageFunctions();
        cleanupOrphanSliderBackgrounds();
        _lastHandledUrl = null;
        _fetchPending = true;
    });

    // turbo:frame-load is the AUTHORITATIVE signal that frame content is ready.
    // It must ALWAYS re-initialize: turbo:load or turbo:render may have already
    // run handleNavigation for the same URL, but the frame content wasn't replaced
    // yet at that point. After frame-load, the DOM is fresh and needs new handlers.
    document.addEventListener('turbo:frame-load', function (e) {
        _fetchPending = false;   // DOM is now guaranteed to be up-to-date
        _lastHandledUrl = null;  // bypass URL dedup — frame content just changed

        // Fast-path: if turbo:render/turbo:load already ran handleNavigation for
        // this page but deferred loadPageModule (because _fetchPending was true),
        // we can skip the redundant cleanup and go straight to module loading.
        var newPage = detectCurrentPage();
        if (newPage === _currentPage && initializedModules.has(newPage)) {
            _loadingPage = null;
            _loadGeneration++;
            // Synchronously reinitialize components on the fresh frame DOM
            if (typeof Facturavia !== 'undefined' && Facturavia.preload) {
                Facturavia.preload();
            }
            initializedModules.clear();
            loadPageModule(newPage);
            return;
        }

        handleNavigation({ domReady: true });
    });
    // turbo:render and turbo:load handle Drive navigations (back/forward, initial).
    // They are deduped by URL so only one of them runs per navigation.
    // When _fetchPending is true, handleNavigation defers loadPageModule to
    // turbo:frame-load. turbo:load also clears the flag as a safety net for
    // Drive navigations (where turbo:frame-load does not fire).
    document.addEventListener('turbo:render', handleNavigation);
    document.addEventListener('turbo:load', function () {
        // If _fetchPending is already false, turbo:frame-load already handled
        // this navigation — skip to avoid double-initializing the page module.
        if (!_fetchPending) return;
        _fetchPending = false;
        // turbo:render may have already called handleNavigation (setting
        // _lastHandledUrl) but deferred loadPageModule because _fetchPending
        // was true.  Reset _lastHandledUrl so the URL-dedup check doesn't
        // block this handler from actually loading the module.
        _lastHandledUrl = null;
        handleNavigation();
    });

    // Intentar carga inicial solo cuando el DOM esté listo.
    // Los scripts se cargan en <head> sin defer, así que al ejecutarse
    // el <body> aún no existe y document.getElementById('main') devuelve null.
    // Esto hacía que isLazyFrame fuera false y el módulo se inicializara
    // antes de que el turbo-frame cargara su contenido.
    function tryInitialLoad() {
        // Habilitar el seguimiento de recursos AHORA, para que capture lo que cargue la página
        // pero no lo que cargó el sistema (Turbo, etc) antes de llamar a initPageLoader.
        enableResourceTracking();

        _currentPage = detectCurrentPage();

        const turboFrame = document.getElementById('main');
        const hasSrc = turboFrame && turboFrame.hasAttribute('src');
        const isLazyFrame = hasSrc && !turboFrame.querySelector('[data-page]');
        if (!isLazyFrame) {
            if (!hasSrc) _lastHandledUrl = window.location.href;
            loadPageModule(_currentPage);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', tryInitialLoad);
    } else {
        tryInitialLoad();
    }
}

/**
 * Registra un nuevo módulo de página dinámicamente
 * Útil para módulos que se definen fuera de este archivo
 *
 * @param {string} pageName - Nombre de la página
 * @param {Function} loader - Función que retorna import() dinámico
 */
function registerPageModule(pageName, loader) {
    pageModules[pageName] = loader;
}

/**
 * Obtiene la lista de módulos registrados
 * Útil para debugging
 */
function getRegisteredModules() {
    return Object.keys(pageModules);
}

export {
    initPageLoader,
    loadPageModule,
    registerPageModule,
    registerPageFunction,
    getRegisteredModules,
    detectCurrentPage
};
